import { useState, useEffect, useCallback, useRef } from 'react';
import { FaPlus, FaEdit, FaUserSlash, FaTimes, FaSave, FaEnvelope, FaInfoCircle } from 'react-icons/fa';
import api from '../../api';
import { useColumnVisibility, ColumnPicker } from '../../components/ColumnPicker';

const empty = { email: '', name: '', role: 'employee', title: '', department: '', employeeId: '' };

// How often to silently re-fetch so the online dot / last-active time in
// the info tooltip stay reasonably fresh without a manual page reload.
const REFRESH_INTERVAL_MS = 30000;

function formatRelative(ts) {
  if (!ts) return 'Never';
  const diffMs = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function PresenceDot({ online, size = 8 }) {
  return (
    <span
      className={`inline-block rounded-full flex-shrink-0 ${online ? 'bg-emerald-400' : 'bg-gray-300'}`}
      style={{
        width: size, height: size,
        boxShadow: online ? '0 0 0 2px rgba(52,211,153,0.25), 0 0 8px 2px rgba(52,211,153,0.75)' : undefined,
      }}
    />
  );
}

// Fixed (not absolute) positioning, with the coordinates computed on hover
// from the icon's actual screen position -- the table card below clips
// overflow for its rounded corners, which was cutting off the popover for
// rows near the bottom of the list. Fixed positioning escapes that clip,
// and we flip the popover to open upward when there's no room below.
const TOOLTIP_WIDTH = 224;
const TOOLTIP_HEIGHT = 112;

function EmployeeInfoTooltip({ user }) {
  const iconRef = useRef(null);
  const [pos, setPos] = useState(null);

  const show = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;
    const openUp = rect.bottom + 8 + TOOLTIP_HEIGHT > window.innerHeight;
    setPos({
      left: Math.min(rect.left, window.innerWidth - TOOLTIP_WIDTH - 8),
      top: openUp ? rect.top - 8 - TOOLTIP_HEIGHT : rect.bottom + 8,
    });
  };
  const hide = () => setPos(null);

  return (
    <span className="inline-flex items-center ml-1.5" onMouseEnter={show} onMouseLeave={hide}>
      <span ref={iconRef} className="inline-flex">
        <FaInfoCircle size={12} className="text-gray-300 hover:text-gray-500 cursor-help" />
      </span>
      {pos && (
        <div
          className="fixed z-50 bg-white border border-gray-100 rounded-xl shadow-lg p-3 text-xs text-gray-600"
          style={{ left: pos.left, top: pos.top, width: TOOLTIP_WIDTH }}
        >
          <div className="flex items-center gap-1.5 mb-2">
            <PresenceDot online={user.online} />
            <span className={`font-semibold ${user.online ? 'text-emerald-600' : 'text-gray-400'}`}>
              {user.online ? 'Online now' : 'Offline'}
            </span>
          </div>
          <p className="mb-1"><span className="text-gray-400">Last active:</span> {formatRelative(user.last_seen_at)}</p>
          <p><span className="text-gray-400">Location:</span> {user.location || 'Unknown'}</p>
        </div>
      )}
    </span>
  );
}

export default function AdminEmployees() {
  const [users, setUsers]       = useState([]);
  const [roles, setRoles]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]         = useState(empty);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.getUsers(), api.getRoles()])
      .then(([u, r]) => { setUsers(u); setRoles(r.map(row => row.name)); })
      .catch(() => setError('Failed to load employees.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  // Silent refresh (no loading spinner) so the online dot / last-active
  // time in the info tooltip update without a manual page reload.
  useEffect(() => {
    const id = setInterval(() => {
      api.getUsers().then(setUsers).catch(() => {});
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const openNew = () => { setForm(empty); setEditingId(null); setShowForm(true); setError(''); };

  const openEdit = (u) => {
    setForm({ email: u.email, name: u.name, role: u.role, title: u.title || '', department: u.department || '', employeeId: u.employee_id || '' });
    setEditingId(u.id);
    setShowForm(true);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await api.updateUser(editingId, { name: form.name, role: form.role, title: form.title, department: form.department, employeeId: form.employeeId, active: true });
        setSuccess('Employee updated.');
      } else {
        await api.createUser(form);
        setSuccess(`Account created! Welcome email sent to ${form.email}.`);
      }
      setShowForm(false);
      load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.name}? They will no longer be able to log in.`)) return;
    try {
      await api.deleteUser(u.id);
      setSuccess(`${u.name} has been deactivated.`);
      load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to deactivate.');
    }
  };

  const handleReactivate = async (u) => {
    try {
      await api.updateUser(u.id, { name: u.name, role: u.role, title: u.title, department: u.department, employeeId: u.employee_id, active: true });
      setSuccess(`${u.name} reactivated.`);
      load();
      setTimeout(() => setSuccess(''), 4000);
    } catch {}
  };

  const displayed = users.filter(u => showInactive ? true : u.active);

  const EMP_COLS = [
    { key: 'email',  label: 'Email'  },
    { key: 'title',  label: 'Title'  },
    { key: 'role',   label: 'Role'   },
    { key: 'status', label: 'Status' },
  ];
  const { visible: empCols, toggle: toggleEmpCol, show: showEmpCol } = useColumnVisibility(EMP_COLS, 'cols_employees');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-10">

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#0c1b4d]">Manage Employees</h1>
            <p className="text-gray-500 text-sm mt-1">Add or manage employee portal accounts.</p>
          </div>
          <button onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors">
            <FaPlus size={12} /> Add Employee
          </button>
        </div>

        {error   && <p className="text-red-600 text-sm mb-4 bg-red-50 px-4 py-2 rounded-lg">{error}</p>}
        {success && <p className="text-green-700 text-sm mb-4 bg-green-50 px-4 py-2 rounded-lg">{success}</p>}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Total Employees', count: users.filter(u => u.role === 'employee').length, color: 'bg-blue-50 border-blue-200 text-blue-700' },
            { label: 'Active',          count: users.filter(u => u.active).length,              color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
            { label: 'Inactive',        count: users.filter(u => !u.active).length,             color: 'bg-gray-50 border-gray-200 text-gray-500' },
          ].map(s => (
            <div key={s.label} className={`rounded-xl border p-4 ${s.color}`}>
              <p className="text-2xl font-bold">{s.count}</p>
              <p className="text-xs font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <input type="checkbox" id="showInactive" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="w-4 h-4 accent-blue-700" />
          <label htmlFor="showInactive" className="text-sm text-gray-600">Show inactive employees</label>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="font-bold text-[#0c1b4d]">{editingId ? 'Edit Employee' : 'Add New Employee'}</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                {!editingId && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Email *</label>
                    <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                      placeholder="employee@marvyitservices.com"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><FaEnvelope size={10} /> A welcome email with login link will be sent automatically.</p>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name *</label>
                  <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="First Last"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Role</label>
                    <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {roles.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Employee ID</label>
                    <input value={form.employeeId} onChange={e => setForm({...form, employeeId: e.target.value})}
                      placeholder="MRV-001"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Job Title</label>
                  <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                    placeholder="e.g. Senior Developer"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Department</label>
                  <input value={form.department} onChange={e => setForm({...form, department: e.target.value})}
                    placeholder="e.g. Engineering"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                {error && <p className="text-red-600 text-xs">{error}</p>}
                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors disabled:opacity-60">
                    <FaSave size={12} /> {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Account'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-slate-50">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <ColumnPicker columns={EMP_COLS} visible={empCols} onToggle={toggleEmpCol} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-slate-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Name</th>
                    {showEmpCol('email')  && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Email</th>}
                    {showEmpCol('title')  && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Title</th>}
                    {showEmpCol('role')   && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Role</th>}
                    {showEmpCol('status') && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>}
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map(u => (
                    <tr key={u.id} className={`border-b border-gray-50 hover:bg-slate-50 transition-colors ${!u.active ? 'opacity-50' : ''}`}>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <p className="font-medium text-[#0c1b4d] flex items-center">
                          {u.name}
                          <EmployeeInfoTooltip user={u} />
                        </p>
                        <p className="text-xs text-gray-400">{u.employee_id || '—'}</p>
                      </td>
                      {showEmpCol('email')  && <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{u.email}</td>}
                      {showEmpCol('title')  && <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{u.title || '—'}</td>}
                      {showEmpCol('role')   && <td className="px-5 py-3 whitespace-nowrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {u.role}
                        </span>
                      </td>}
                      {showEmpCol('status') && <td className="px-5 py-3 whitespace-nowrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                          {u.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>}
                      <td className="px-5 py-3">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => openEdit(u)} className="p-2 text-gray-400 hover:text-[#0c1b4d] hover:bg-blue-50 rounded-lg transition-colors">
                            <FaEdit size={13} />
                          </button>
                          {u.active ? (
                            <button onClick={() => handleDeactivate(u)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Deactivate">
                              <FaUserSlash size={13} />
                            </button>
                          ) : (
                            <button onClick={() => handleReactivate(u)} className="px-2 py-1 text-xs text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors font-medium">
                              Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
